import { cn } from "@/lib/utils"

function Skeleton({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="skeleton"
      aria-hidden="true"
      className={cn("skeleton-pulse rounded-2xl bg-muted", className)}
      {...props}
    />
  )
}

export { Skeleton }

/** Composes the shared primitive to match the app's icon / text / metadata rows. */
export function ListSkeleton({ rows = 6, compact = false, className }: {
  rows?: number
  compact?: boolean
  className?: string
}) {
  return (
    <div role="status" aria-label="Loading content" className={cn("w-full min-w-0 divide-y divide-border/40", className)}>
      <span className="sr-only">Loading…</span>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className={cn("flex items-center gap-3 px-2", compact ? "py-2" : "py-3")}>
          <Skeleton className="size-4 shrink-0 rounded" />
          <div className="min-w-0 flex-1 space-y-1.5">
            <Skeleton className={cn("h-4 rounded", i % 2 ? "w-1/2" : "w-2/3")} />
            {!compact && <Skeleton className="h-3 w-1/3 rounded" />}
          </div>
          <Skeleton className="h-4 w-12 shrink-0 rounded" />
        </div>
      ))}
    </div>
  )
}

export function DetailSkeleton({ className, fields = 4 }: { className?: string; fields?: number }) {
  return (
    <div role="status" aria-label="Loading content" className={cn("mx-auto w-full max-w-3xl space-y-6 px-6 py-5", className)}>
      <span className="sr-only">Loading…</span>
      <Skeleton className="h-6 w-1/3 rounded" />
      {Array.from({ length: fields }, (_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-4 w-24 rounded" />
          <Skeleton className={cn("w-full rounded-lg", i === 1 ? "h-24" : "h-9")} />
        </div>
      ))}
    </div>
  )
}

export function AnalyticsSkeleton() {
  return (
    <div role="status" aria-label="Loading analytics" className="mx-auto w-full max-w-6xl space-y-6 overflow-hidden px-6 py-5">
      <span className="sr-only">Loading…</span>
      <div className="flex justify-end"><Skeleton className="h-9 w-40 rounded-lg" /></div>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        {Array.from({ length: 6 }, (_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}
      </div>
      <Skeleton className="h-72 w-full rounded-xl" />
      <ListSkeleton rows={4} />
    </div>
  )
}

export function ChatSkeleton() {
  return (
    <div role="status" aria-label="Loading conversation" className="mx-auto flex h-full w-full max-w-3xl flex-col gap-8 overflow-hidden px-6 py-6">
      <span className="sr-only">Loading…</span>
      <Skeleton className="ml-auto h-16 w-2/3 rounded-2xl" />
      <div className="space-y-3">
        <Skeleton className="h-4 w-1/3 rounded" />
        <Skeleton className="h-4 w-full rounded" />
        <Skeleton className="h-4 w-5/6 rounded" />
        <Skeleton className="h-4 w-2/3 rounded" />
      </div>
      <Skeleton className="mt-auto h-24 w-full rounded-3xl" />
    </div>
  )
}

export function ContentSkeleton({ className }: { className?: string }) {
  return (
    <div role="status" aria-label="Loading content" className={cn("w-full space-y-6", className)}>
      <span className="sr-only">Loading…</span>
      {Array.from({ length: 3 }, (_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-4 w-full rounded" />
          <Skeleton className="h-4 w-5/6 rounded" />
          <Skeleton className="h-4 w-2/3 rounded" />
        </div>
      ))}
    </div>
  )
}
